import { ApiFetch } from '../../../shared/utils/fetch'
import type { ApiOrdersResponse, OrdersParams, ApiOrder, Order } from '../types/orders.types'
import { transformApiOrderToOrder } from '../types/orders.types'

export const realOrdersApi = {
  // Get all orders from the real API
  getOrders: async (params?: OrdersParams): Promise<{ orders: Order[], meta: ApiOrdersResponse['meta'] }> => {
    const searchParams = new URLSearchParams()
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, v))
          } else {
            searchParams.append(key, String(value))
          }
        }
      })
    }
    
    const queryString = searchParams.toString()
    const endpoint = `/orders${queryString ? `?${queryString}` : ''}`
    
    const response = await ApiFetch.get<unknown>(endpoint)

    let rawOrders: ApiOrder[] = []
    let meta: ApiOrdersResponse['meta'] = {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: '',
      endCursor: '',
      totalCount: 0,
      lastCursor: '',
    }
    
    if (Array.isArray(response)) {
      rawOrders = response
      meta.totalCount = response.length
    } else if (response && typeof response === 'object') {
      const body = response as { data?: unknown; meta?: Partial<ApiOrdersResponse['meta']> }
      if (Array.isArray(body.data)) {
        rawOrders = body.data as ApiOrder[]
        if (body.meta) {
          meta = { ...meta, ...body.meta }
        } else {
          meta.totalCount = rawOrders.length
        }
      } else {
        rawOrders = []
      }
    }
    
    const orders = rawOrders.map(transformApiOrderToOrder)
    
    return {
      orders,
      meta
    }
  },

  // Get a single order by ID
  getOrder: async (orderId: string): Promise<Order> => {
    const response = await ApiFetch.get<unknown>(`/orders/${orderId}`)
    const apiOrder = (response && typeof response === 'object' && 'data' in response)
      ? (response as { data: ApiOrder }).data
      : (response as ApiOrder)
    return transformApiOrderToOrder(apiOrder)
  },

  // Update order status
  updateOrderStatus: async (orderId: string, status: ApiOrder['status']): Promise<Order> => {
    const response = await ApiFetch.put<unknown>(`/orders/${orderId}/status`, { status })
    const apiOrder = (response && typeof response === 'object' && 'data' in response)
      ? (response as { data: ApiOrder }).data
      : (response as ApiOrder)
    return transformApiOrderToOrder(apiOrder)
  },

  // Mark an order as dispatched via a manual (non-DHL) courier
  markManualShipment: async (orderId: string, data: { carrier: string; trackingNumber: string }): Promise<Order> => {
    const response = await ApiFetch.post<unknown>(`/orders/${orderId}/shipment/manual`, data)
    const apiOrder = (response && typeof response === 'object' && 'data' in response)
      ? (response as { data: ApiOrder }).data
      : (response as ApiOrder)
    return transformApiOrderToOrder(apiOrder)
  },
}